<template>
  <div class="_grid">
    <div
      v-for="item in previewItems"
      :key="item.themeId"
      class="flex flex-col group"
    >
      <!-- 预览图 -->
      <div
        class="relative flex items-center justify-center border-box overflow-hidden w-50 h-50 mb-4 bg-[#404040] rounded-2xl border-[2px] border-solid border-[transparent] group-hover:border-[#0D84F2]"
      >
        <img
          v-if="previewSrcs[item.themeId]?.base64"
          :src="previewSrcs[item.themeId]?.base64"
          draggable="false"
          class="transition-all"
          :style="{
            height: '160px'
          }"
        />
        <div
          v-if="
            !previewSrcs[item.themeId]?.base64 &&
            !previewSrcs[item.themeId]?.loading
          "
          class="text-red-500"
        >
          未上传动图
        </div>

        <!-- 正在生成时：添加一个loading -->
        <div
          v-if="previewSrcs[item.themeId]?.loading"
          class="w-full h-full absolute top-0 left-0 right-0 bottom-0 bg-black/90 flex flex-col justify-center items-center"
        >
          <a-spin
            size="large"
            :tip="
              status == 'recording'
                ? '处理中：遍历数据...'
                : status == 'ffmpegDownloading'
                  ? '处理中：下载依赖...'
                  : status == 'toWebping'
                    ? '处理中：生成预览图...'
                    : '处理中...'
            "
          />
        </div>

        <!-- 生成按钮 -->
        <div
          v-if="!previewSrcs[item.themeId]?.loading"
          class="absolute top-0 left-0 right-0 bottom-0 flex flex-col justify-center items-center gap-5 bg-[#000]/[0.7] opacity-0 group-hover:opacity-[1]"
        >
          <a-button class="" @click="() => uploadImage(item)"
            >上传动图</a-button
          >
          <a-button class="" type="primary" @click="() => _regenerateWebp(item)"
            >刷新</a-button
          >
        </div>
      </div>

      <!-- 预览图介绍内容 -->
      <div class="text-white w-52 overflow-hidden pt-1 px-2.5 pb-2">
        <div class="text-lg leading-none pb-2">
          {{ item.title }}
        </div>
        <div class="text-sm leading-none font-thin pb-2 opacity-[0.6] flex">
          <div class="flex-1">
            {{ `尺寸: ${item.width}*${item.height}` }}
          </div>
          <div>格式: {{ item.exts.join('/') }}</div>
        </div>
        <div class="text-sm leading-none font-thin pb-2 opacity-[0.6]">
          {{
            `大小: <${filesize(item.limitSize, {
              base: 2,
              standard: 'jedec'
            })}`
          }}
        </div>
        <div
          v-if="previewSrcs[item.themeId]?.base64 && !validSize(item.themeId)"
          class="text-[#E66138] text-[13px]"
        >
          尺寸有误，请刷新或重新上传！
        </div>
      </div>
    </div>
  </div>
  <!-- canvas -->
  <canvas
    ref="canvasRef"
    class="absolute top-0 left-0 z-[-20]"
    :width="watchfaceStore.size.width"
    :height="watchfaceStore.size.height"
  />
</template>
<script setup lang="ts">
import { useWatchfaceAnimation } from '@/hooks/use-watchface-animation';
import { ElectronApi } from '@/services/electron.api';
import { WatchfaceService } from '@/services/watchface';
import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { getFileSize, getImageSize } from '@/utils/fs/image.utils';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { Modal, notification } from 'ant-design-vue';
import { filesize } from 'filesize';
import { computed, ref, watch } from 'vue';
import { sleep } from '@/utils/sleep.utils';
const props = defineProps({
  watchfacePath: {
    type: String,
    required: true
  }
});

const layerStore = useLayerStore();

interface AnimationImageItem {
  title: string;
  width: number;
  height: number;
  themeId: string;
  theme: StoreThemeItem;
  limitSize: number;
  exts: string[];
  _basename: string;
  _absolutePath: string;
}

const previewSrcs = ref<{
  [themeId: string]: {
    base64: string;
    width: number;
    height: number;
    loading?: boolean;
  };
}>({});

const previewItems = computed(() => {
  const res: AnimationImageItem[] = [];
  const service = new WatchfaceService(props.watchfacePath);

  // 亮屏动态预览图（所有设备都需要，IFTTT效果在录制时由 _getIftttSourceItems 按设备能力控制）
  layerStore.themes
    .filter(
      (item) => item.type == 'normal'
      //  ||
      // (item.type == 'AOD' && canUse('animation-in-AOD').valid)
    )
    .forEach((theme, index) => {
      // const isAOD = theme.type == 'AOD' && canUse('animation-in-AOD').valid;
      // const title = theme.name + (isAOD ? '息屏' : '亮屏') + '动态预览图';
      const title = theme.name + '动态预览图';
      const _basename = 'style_' + (theme.id ?? '') + '_animated';
      const _absolutePath = service.preview.getAbsolutePath(_basename) ?? '';
      res.push({
        title: title,
        exts: ['webp', 'gif'],
        ...watchfaceStore.size,
        themeId: theme.id ?? '',
        theme,
        limitSize: 1024 * 3.5 * 1024,
        _basename,
        _absolutePath
      });
    });

  return res;
});

const watchfaceStore = useWatchfaceStore();

// 重新生成预览图
const canvasRef = ref();
const { exportWebp, initCanvas, status } = useWatchfaceAnimation(canvasRef);

const _regenerateWebp = async (previewItem: AnimationImageItem) => {
  const { theme, _absolutePath, themeId, _basename } = previewItem;

  // 如果其他预览图正在生成，则不生成
  if (
    previewItems.value.some(
      (item) =>
        // 其他的预览图
        item.themeId !== themeId &&
        // 正在生成
        previewSrcs.value[item.themeId]?.loading
    )
  ) {
    notification.warning({
      message: '正在生成其他预览图，请稍后再试'
    });
    return;
  }

  const _srcItem = previewSrcs.value[themeId] ?? {};
  try {
    _srcItem.loading = true;
    if (!theme) return;
    const options = await initCanvas(theme, theme?.colorGroupTable?.[0]);
    // 如果存在rive,则需要等待300ms在进行录制
    if (options?.rive) {
      await sleep(450);
    }
    const webp = await exportWebp(_absolutePath + '.webp');
    if (!webp) return;

    //修改theme上的预览图属性值
    theme.previewAni = _basename + '.webp';

    await readPreviewBaseUrls(theme.id);

    _srcItem.loading = false;
  } catch (error) {
    console.log(error);
  } finally {
    _srcItem.loading = false;
  }
};

const readPreviewBaseUrls = async (_themeId?: string) => {
  const _watchfacePath = props.watchfacePath;
  if (!_watchfacePath) return;
  const service = new WatchfaceService(_watchfacePath);
  for (const previewItem of previewItems.value) {
    const { theme, themeId } = previewItem;
    if (_themeId && themeId !== _themeId) continue;

    if (!previewSrcs.value[themeId])
      previewSrcs.value[themeId] = {
        base64: '',
        width: 0,
        height: 0
      };

    // 读取预览图
    const previewAni = theme.previewAni ?? '';
    const baseUrl = await service.preview.getPreviewImage(previewAni);

    if (!baseUrl) continue;
    // 获取图片尺寸，用于后续校验，防止预览图尺寸错误
    const { width = 0, height = 0 } =
      (await getImageSize(undefined, baseUrl)) ?? {};

    if (width && height) {
      Object.assign(previewSrcs.value[themeId], {
        base64: baseUrl,
        width,
        height
      });
    }
  }
};

watch(previewItems, () => readPreviewBaseUrls(), { deep: true });

defineExpose({
  initData: async () => {
    await readPreviewBaseUrls();
  },
  regenerateAll: async () => {
    for (const item of previewItems.value) {
      await _regenerateWebp(item);
    }
  }
});

const uploadImage = async (item: AnimationImageItem) => {
  const _watchfacePath = props.watchfacePath;
  const {
    width,
    height,
    theme,
    limitSize,
    exts,
    _absolutePath,
    _basename,
    themeId
  } = item;

  const imagePath = await pickFile({
    extensions: [...exts],
    multi: false
  }).then((r) => r?.[0]);
  if (!imagePath) return;
  // 验证文件大小
  const fsize = (await getFileSize(imagePath)) as number;
  if (fsize > limitSize) {
    Modal.warn({
      title: '图片错误',
      content: `图片体积不可超过${filesize(limitSize, {
        base: 2,
        standard: 'jedec'
      })}`,
      zIndex: 9999
    });
    return;
  }

  // 验证图片尺寸
  const size = await getImageSize(imagePath);
  if (size?.width !== width || size.height !== height) {
    Modal.warn({
      title: '图片错误',
      content: `图片尺寸错误，图片大小必须为${width}*${height}px`,
      zIndex: 9999
    });
    return;
  }

  // 保存
  // 获取扩展名
  const ext = ElectronApi.path?.extname(imagePath);
  await ElectronApi.fs?.promises.cp(imagePath, _absolutePath + ext);

  // 修改theme上的预览图属性值
  theme.previewAni = _basename + ext;

  await readPreviewBaseUrls(themeId);
};

const validSize = (themeId: string) => {
  const item = previewItems.value?.find((item) => item.themeId === themeId);
  const item2 = previewSrcs.value[themeId] ?? {};
  if (!item2) return true;
  return item?.width === item2.width && item?.height === item2.height;
};
</script>

<style scoped lang="less">
._grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 200px);
  column-gap: 30px;
  row-gap: 15px;
  justify-content: center;
}
</style>
